<template>
    <v-card v-if="registrySchema != null">
        <v-card-title>
            <v-row>
                <v-col>
                    {{ registrySchema.displayName }}
                </v-col>
                <v-spacer />
                <v-col v-if="userCanManageRegistrySchema" class="text-right">
                    <v-btn color="primary" @click="viewEntries" class="mr-3">View Entries</v-btn>
                    <v-btn color="primary" @click="showEditSchemaForm" class="mr-3">Edit Schema</v-btn>
                    <v-btn color="primary" @click="showAddAttributeForm">Add Attribute</v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-card-subtitle
            >ID {{ registrySchema.id }}
            <br />
            {{ registrySchema.isBenchling ? "Benchling Schema" : "Invaio Schema" }}</v-card-subtitle
        >
        <v-card-text>
            <schema-attributes-list
                :attributes="registrySchema.attributes"
                :is-registry-schema="true"
                :allow-update="userCanManageRegistrySchema"
                @showEditAttributeForm="showEditAttributeForm"
                @deleteAttribute="deleteAttribute"
            />
        </v-card-text>

        <v-dialog v-if="editAttribute" v-model="editAttributeFormVisible" persistent>
            <schema-attribute-form
                :all-registry-schemas="allRegistrySchemas"
                :attribute="editAttribute"
                :thing-schema="registrySchema"
                :isNewAttribute="isNewAttribute"
                :is-registry-schema="true"
                @closeEditAttributeForm="closeEditAttributeForm"
                @saveAttribute="saveAttribute"
            />
        </v-dialog>

        <v-dialog v-model="editSchemaFormVisible" persistent>
            <registry-schema-form
                :registry-schema="registrySchema"
                @cancelForm="closeCreateRegistrySchemaForm"
                @registrySchemaSaved="savedRegistrySchema"
            />
        </v-dialog>
    </v-card>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { withLoadingScreen } from "@/lib/loading";
import { upsert } from "@/lib/arrayUtils";
import SchemaAttributeForm from "../SchemaConfig/SchemaAttributeForm.vue";
import SchemaAttributesList from "../SchemaConfig/SchemaAttributesList.vue";
import {
    registriesAPI,
    SavedRegistrySchema,
    UpdateRegistrySchema,
} from "@/services/registries/registriesAPI";
import { useAuth } from "@/lib/ts/auth";
import { Attribute } from "@/services/things/models";
import { AttributeItem } from "@/components/SchemaConfig/SchemaAttributesList.vue";
import { useSchemaAttributeForm } from "@/components/SchemaConfig/schemaAttributeFormComposable";
import RegistrySchemaForm from "@/components/Registries/RegistrySchemaForm.vue";

const route = useRoute();
const auth = useAuth();
const router = useRouter();

const registrySchemaName = ref<string>(route.params.name as string);
const registrySchema = ref<SavedRegistrySchema>();
const editSchemaFormVisible = ref(false);

const {
    allRegistrySchemas,
    editAttribute,
    editAttributeFormVisible,
    isNewAttribute,
    showAddAttributeForm,
    showEditAttributeForm,
    closeEditAttributeForm,
    initialize
} = useSchemaAttributeForm()

async function reloadSchema() {
    await withLoadingScreen(async () => {
        await initialize();
        registrySchema.value = allRegistrySchemas.value.find(
            (registrySchema) => registrySchema.name === registrySchemaName.value
        );
    });
}

reloadSchema();

function viewEntries() {
    router.push({ path: `/registries/entries/${registrySchema.value!.name}` });
}

function showEditSchemaForm() {
    editSchemaFormVisible.value = true;
}

function closeCreateRegistrySchemaForm() {
    editSchemaFormVisible.value = false;
}

async function savedRegistrySchema() {
    editSchemaFormVisible.value = false;
    await reloadSchema();
}

async function saveAttribute(updatedAttribute: Attribute) {
    const updatedAttributes = upsert(
        registrySchema.value!.attributes,
        updatedAttribute,
        (item) => updatedAttribute.name === item.name
    );

    await saveRegistrySchemaAttributes(updatedAttributes);
}

async function deleteAttribute(attributeItem: AttributeItem) {
    if (confirm(`Warning! Deleting ${attributeItem.name} may result in data loss. Are you sure you want to delete ${attributeItem.name}?`)) {
        const currentAttributes = registrySchema.value!.attributes;
        const updatedAttributes = currentAttributes.filter((currentAttribute) => {
            return currentAttribute.name !== attributeItem.name
        });

        await saveRegistrySchemaAttributes(updatedAttributes);
    }
}

async function saveRegistrySchemaAttributes(updatedAttributes: Attribute[]) {
    const updatedSchema = {
        ...registrySchema.value,
        attributes: updatedAttributes,
    } as UpdateRegistrySchema;

    await withLoadingScreen(async () => {
        await registriesAPI.updateRegistrySchema(updatedSchema);
        closeEditAttributeForm();
        await reloadSchema();
    });
}

const userCanManageRegistrySchema = computed(() => {
    return auth.isAuthorized("Registries:Manage Schema");
});

</script>
